import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import type { Auth } from "@redotech/redo-merchant-app-common/auth";
import {
  type NotificationBannerItem,
  RedoNotificationBanner,
} from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { memo, useState } from "react";
import { autoOnboardingUrl } from "../../../onboarding";
import type { ReturnsOnboardingResumeState } from "../../../onboarding/returns-onboarding-resume-state";

const DISMISSAL_KEY_PREFIX = "returns-onboarding-resume-banner-dismissed";

export function returnsOnboardingResumeBannerDismissalKey(teamId: string) {
  return `${DISMISSAL_KEY_PREFIX}:${teamId}`;
}

export interface ReturnsOnboardingResumeBannerProps {
  auth: Pick<Auth, "teamId" | "token">;
  state: NonNullable<ReturnsOnboardingResumeState>;
  teamId: string;
}

export const ReturnsOnboardingResumeBanner = memo(
  function ReturnsOnboardingResumeBanner({
    auth,
    state,
    teamId,
  }: ReturnsOnboardingResumeBannerProps) {
    const { t } = useTranslation();
    const key = returnsOnboardingResumeBannerDismissalKey(teamId);
    const [dismissedKeys, setDismissedKeys] = useState<string[]>([]);
    const onDismiss = useHandler(() => {
      sessionStorage.setItem(key, "true");
      setDismissedKeys((keys) => (keys.includes(key) ? keys : [...keys, key]));
    });

    if (dismissedKeys.includes(key) || sessionStorage.getItem(key) === "true") {
      return null;
    }

    const url = autoOnboardingUrl(
      auth.token,
      auth.teamId,
      "resume-banner",
    ).toString();
    const items: NotificationBannerItem[] = [
      {
        id: key,
        message: t(
          "Finish Returns setup — {{completedSteps}} of {{totalSteps}} done",
          {
            completedSteps: state.completedSteps,
            totalSteps: state.totalSteps,
          },
        ),
        type: "info",
        actions: [
          {
            actionType: "link",
            href: url,
            internal: false,
            target: "_blank",
            text: t("Continue setup"),
          },
        ],
      },
    ];

    return <RedoNotificationBanner items={items} onDismiss={onDismiss} />;
  },
);
